import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Megaphone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnnouncementBanner = ({ placement = 'above_header' }) => {
  const { isAuthenticated } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    const stored = localStorage.getItem('dismissedAnnouncements');
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [isAuthenticated]);

  const fetchAnnouncements = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem('zazu_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_URL}/api/announcements`, { headers });
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const handleDismiss = (announcementId) => {
    const newDismissed = [...dismissedIds, announcementId];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
  };

  // Filter announcements by placement and dismissed status
  const visibleAnnouncements = announcements.filter(
    ann => ann.placement === placement && !dismissedIds.includes(ann.id)
  );

  if (visibleAnnouncements.length === 0) return null;

  return (
    <div className="announcement-banner-container">
      {visibleAnnouncements.map((announcement) => (
        <div
          key={announcement.id}
          className="relative flex items-center justify-center px-4 py-2.5 text-sm font-medium"
          style={{
            backgroundColor: announcement.background_color,
            color: announcement.text_color
          }}
          data-testid={`announcement-${announcement.id}`}
        >
          <div className="flex items-center gap-2 max-w-4xl mx-auto">
            <Megaphone className="w-4 h-4 flex-shrink-0" />
            <span>{announcement.message}</span>
            {announcement.link_url && (
              <a
                href={announcement.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline hover:opacity-80 transition-opacity ml-2"
                style={{ color: announcement.text_color }}
              >
                {announcement.link_text || 'Learn more'}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          
          {announcement.is_dismissible && (
            <button
              onClick={() => handleDismiss(announcement.id)}
              className="absolute right-3 p-1 rounded hover:opacity-70 transition-opacity"
              style={{ color: announcement.text_color }}
              aria-label="Dismiss announcement"
              data-testid={`dismiss-announcement-${announcement.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
